import { Stack } from '@mui/material';

import {
  formatLicensePlateLabel,
  getActiveLicensePlate,
} from '@/layouts/TabLayout/MasterVehicles/licensePlate.utils';
import { IOffenseOptionItem, IProperty, PropertyTypeTag } from '@/models';
import { flattenObject } from '@/utils';

import PropertyTypeCell from './PropertyTypeCell';
import { nesterRowColumns } from '../../data';

interface PropertyTypeRowProps {
  row: IProperty;
  propertyType: Map<string, IOffenseOptionItem>;
  paddingLeft?: number;
}

const PropertyTypeRow = ({
  row,
  propertyType,
  paddingLeft = 14,
}: PropertyTypeRowProps) => {
  if (!row || row.typeTag === PropertyTypeTag.DIGITAL || !row.type) {
    return;
  }
  const extractValue = (
    value: [any, any],
    flattenRow: { [key: string]: any },
  ) => {
    const [optionsList, optionsKey] = value;
    const keyValue = flattenRow[optionsKey as keyof typeof flattenRow];
    return Array.isArray(keyValue)
      ? keyValue.map((item) => (
          <>
            {valueCheck(optionsList, item)}
            <br />
          </>
        ))
      : valueCheck(optionsList, keyValue);
  };

  const valueCheck = (list: Record<string, any>, key: string | number) => {
    return list instanceof Map ? list.get(key) : list[key];
  };

  const render = (
    itemColumns: { label: any; value: any }[],
    data: Record<string, any>,
    key?: number,
  ) => {
    return (
      <Stack
        key={key}
        sx={{
          backgroundColor: ' rgba(238, 62, 92, 0.05)',
          display: 'grid',
          gridTemplateColumns: '320px 125px 200px 200px',
          pl: paddingLeft,
        }}
      >
        {itemColumns?.map(({ label, value }, index) => {
          const cellValue = Array.isArray(value)
            ? extractValue(value as [any, any], data)
            : data[value];
          return (
            <>
              <PropertyTypeCell
                key={index}
                value={cellValue}
                label={label}
                index={index}
              />
            </>
          );
        })}
      </Stack>
    );
  };

  const nestedRowColumnsOptions = nesterRowColumns(propertyType);
  const columnsToRender: { label: any; value: any }[] =
    nestedRowColumnsOptions[row.type];
  if (!columnsToRender?.length) {
    return;
  }

  let flatData: Record<string, any> | Array<Record<string, any>> = {};
  switch (row.type?.toString()) {
    case propertyType.get('ALCOHOL')?.key: {
      if (!row.alcohol) return;
      flatData = flattenObject(row.alcohol);
      break;
    }
    case propertyType.get('MONEY')?.key: {
      if (!row.cash) return;
      flatData = flattenObject(row.cash);
      break;
    }
    case propertyType.get('CREDIT_DEBIT_CARDS')?.key: {
      if (!row.creditCard) return;
      flatData = flattenObject(row.creditCard);
      break;
    }
    case propertyType.get('JEWELRY_PRECIOUS_METALS')?.key: {
      if (!row.jewelry) return;

      flatData = flattenObject(row.jewelry);
      break;
    }
    case propertyType.get('EXPLOSIVES')?.key: {
      if (!row.explosive) return;
      flatData = flattenObject(row.explosive);
      break;
    }
    case propertyType.get('FIREARMS')?.key: {
      if (!row.firearm) return;
      flatData = flattenObject(row.firearm);
      break;
    }
    case propertyType.get('OTHER_WEAPONS')?.key: {
      if (!row.knife) return;
      flatData = flattenObject(row.knife);
      break;
    }
    case propertyType.get('DRUGS_NARCOTICS')?.key: {
      if (!row.drugs?.length) return;
      flatData = row.drugs.map((drugItem) => flattenObject(drugItem));

      break;
    }
    case propertyType.get('AUTOMOBILES')?.key: {
      if (!row.vehicle) return;
      flatData = {
        ...flattenObject(row.vehicle),
        masterVehicleLicensePlate: formatLicensePlateLabel(
          getActiveLicensePlate(row.vehicle.masterVehicle),
        ),
      };
      break;
    }
    default:
      return;
  }

  if (!Array.isArray(flatData) && !Object.keys(flatData).length) {
    return;
  }

  return Array.isArray(flatData)
    ? flatData.map((item, index) => render(columnsToRender, item, index))
    : render(columnsToRender, flatData);
};

export default PropertyTypeRow;
